import { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  ThemeIcon,
  SimpleGrid,
  Paper,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconRocket, IconStar } from '@tabler/icons-react';
import { useApiClient, useLiveQuery, useSession } from '../../api';
import { formatCents } from '../products/productDisplay';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Profile boost / featured placement (contract §4.21). Fixed 3/7/30-day
// catalog from GET /me/seller/boost; buying POSTs boost-checkout and leaves
// the SPA for CCBill — the webhook extends featured_until and drops a
// boost.activated notification, so the card listens on the notifications
// topic to refresh itself when the seller returns.
//
// `redirect` is injectable because jsdom's window.location.assign is
// LegacyUnforgeable (can't be spied/stubbed) — tests pass a spy; production
// uses the default.
const BoostCard = ({ redirect = (url) => window.location.assign(url) }) => {
  const api = useApiClient();
  const { me } = useSession();
  const [buyingDays, setBuyingDays] = useState(null);

  const { data: boost, loading, error, refetch } = useLiveQuery(
    '/api/v1/me/seller/boost',
    null,
    { topics: [me && `user.${me.clerk_user_id}.notifications`] }
  );

  const handleBuy = async (days) => {
    if (buyingDays) return;
    setBuyingDays(days);
    try {
      const { url } = await api.post('/api/v1/me/seller/boost-checkout', { days });
      redirect(url); // keep the button loading while the browser navigates away
    } catch (err) {
      let message = err?.message || 'Could not start the boost checkout';
      if (err?.code === 'config') message = "Payments aren't configured yet — please try again later.";
      notifications.show({ title: 'Error', message, color: 'red' });
      setBuyingDays(null);
    }
  };

  if (error) {
    return (
      <Card withBorder radius="md" padding="md">
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">Couldn't load your boost status.</Text>
          <Button variant="subtle" size="xs" onClick={() => refetch()}>
            Retry
          </Button>
        </Group>
      </Card>
    );
  }
  if (loading || !boost) return null;

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon size="sm" color="yellow" variant="light" radius="xl">
              <IconRocket size="0.8rem" />
            </ThemeIcon>
            <Text fw={600} size="sm">Profile Boost</Text>
          </Group>
          {boost.is_featured && (
            <Badge color="yellow" variant="filled" leftSection={<IconStar size="0.7rem" />}>
              Featured
            </Badge>
          )}
        </Group>

        {boost.is_featured ? (
          <Text size="sm">
            Your profile is featured until{' '}
            <Text component="span" fw={600}>{formatDate(boost.featured_until)}</Text>.
            Buying another boost extends it.
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            Get featured at the top of the princess directory — more eyes on your
            profile, more spoiling.
          </Text>
        )}

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          {(boost.options ?? []).map((option) => (
            <Paper key={option.days} withBorder p="md" radius="md">
              <Stack gap="xs" align="center">
                <Text fw={700} size="lg">{option.days} days</Text>
                <Text size="sm" c="dimmed">{formatCents(option.price_cents)}</Text>
                <Button
                  variant="light"
                  color="yellow"
                  size="xs"
                  fullWidth
                  leftSection={<IconRocket size="0.8rem" />}
                  onClick={() => handleBuy(option.days)}
                  loading={buyingDays === option.days}
                  disabled={buyingDays !== null && buyingDays !== option.days}
                >
                  {boost.is_featured ? 'Extend' : 'Boost'}
                </Button>
              </Stack>
            </Paper>
          ))}
        </SimpleGrid>
      </Stack>
    </Card>
  );
};

export default BoostCard;
